import { useEffect, useMemo, useState } from 'react'
import type { Affix, AffixSourceKind, FixedEquipmentBase, FixedEquipmentPayload, FixedEquipmentSlot, FixedItemSelection, FixedItemSelections, ModifierRoll } from './planner-types'

const emptySelection = (): FixedItemSelection => ({
  baseItemId: '',
  itemName: '',
  itemLevel: '100',
  implicitRolls: {},
  selectedAffixIds: [],
  affixRolls: {},
  affixSources: {},
})

function compileRollMap(rolls: Record<string, Record<string, string>>, label: string): ModifierRoll[] {
  return Object.entries(rolls).map(([modifierId, stats]) => {
    const entries = Object.entries(stats)
    if (entries.length === 0) throw new Error(`${label} ${modifierId} has no validated stat fields loaded; reopen the item before calculating.`)
    const values: Record<string, number> = {}
    for (const [statId, raw] of entries) {
      const text = raw.trim()
      if (!text) throw new Error(`Enter an exact ${label} roll for ${statId}.`)
      const value = Number(text)
      if (!Number.isFinite(value)) throw new Error(`${statId} must be numeric.`)
      values[statId] = value
    }
    return { modifier_id: modifierId, stat_values: values }
  })
}

function compileSelectedAffixes(selection: FixedItemSelection, source: AffixSourceKind, label: string): ModifierRoll[] {
  const rolls: Record<string, Record<string, string>> = {}
  for (const modifierId of selection.selectedAffixIds) {
    const selectedSource = selection.affixSources[modifierId]
    if (!selectedSource) throw new Error(`${label} ${modifierId} has no validated source classification; reopen the item before calculating.`)
    if (selectedSource === source) rolls[modifierId] = selection.affixRolls[modifierId] || {}
  }
  return compileRollMap(rolls, label)
}

export function compileFixedEquipment(selections: FixedItemSelections): FixedEquipmentPayload[] {
  return Object.entries(selections)
    .filter(([, selection]) => Boolean(selection.baseItemId))
    .map(([slotId, selection]) => {
      const itemLevel = Number(selection.itemLevel)
      if (!Number.isInteger(itemLevel) || itemLevel < 1 || itemLevel > 100) throw new Error(`${slotId} item level must be 1–100.`)
      if (selection.selectedAffixIds.length > 6) throw new Error(`${slotId} cannot contain more than six explicit modifiers.`)
      return {
        slot_id: slotId,
        base_item_id: selection.baseItemId,
        item_level: itemLevel,
        implicit_modifier_rolls: compileRollMap(selection.implicitRolls, `${slotId} implicit`),
        natural_modifier_rolls: compileSelectedAffixes(selection, 'natural', `${slotId} natural modifier`),
        crafted_modifier_rolls: compileSelectedAffixes(selection, 'crafted', `${slotId} crafted modifier`),
      }
    })
}

type Props = {
  apiUrl: string
  slot: FixedEquipmentSlot | undefined
  selection: FixedItemSelection | undefined
  onChange: (selection: FixedItemSelection) => void
  onClear: () => void
  onError: (message: string) => void
}

function conflictReason(affix: Affix, selected: Affix[]): string | null {
  if (selected.some(value => value.modifier_id === affix.modifier_id)) return null
  if (selected.length >= 6) return 'Six-modifier rare limit reached.'
  if (selected.filter(value => value.generation_type_id === affix.generation_type_id).length >= 3) {
    return `Three ${affix.generation_type_id === 'PREFIX' ? 'prefixes' : 'suffixes'} already selected.`
  }
  if (affix.source_kind === 'crafted' && selected.some(value => value.source_kind === 'crafted')) return 'A crafted modifier is already selected.'
  const occupied = new Set(selected.flatMap(value => value.families || []))
  const shared = (affix.families || []).find(family => occupied.has(family))
  return shared ? `Conflicts with modifier family ${shared}.` : null
}

export default function FixedEquipmentEditor({ apiUrl, slot, selection, onChange, onClear, onError }: Props) {
  const current = selection || emptySelection()
  const [bases, setBases] = useState<FixedEquipmentBase[]>([])
  const [affixes, setAffixes] = useState<Affix[]>([])
  const [search, setSearch] = useState('')
  const [affixSearch, setAffixSearch] = useState('')
  const [loadingBases, setLoadingBases] = useState(false)
  const [loadingAffixes, setLoadingAffixes] = useState(false)

  useEffect(() => {
    setBases([])
    setSearch('')
    if (!slot) return
    let cancelled = false
    setLoadingBases(true)
    fetch(`${apiUrl}/api/catalog/equipment/slots/${encodeURIComponent(slot.slot_id)}/bases`)
      .then(response => {
        if (!response.ok) throw new Error(`Fixed equipment base catalog failed (${response.status})`)
        return response.json() as Promise<FixedEquipmentBase[]>
      })
      .then(rows => {
        if (cancelled) return
        setBases(rows)
        if (current.baseItemId) {
          const base = rows.find(row => row.base_item_id === current.baseItemId)
          if (!base) {
            onClear()
            return
          }
          const implicitRolls = Object.fromEntries(base.implicit_modifiers.map(modifier => [
            modifier.modifier_id,
            Object.fromEntries(modifier.stats.map(stat => [stat.stat_id, current.implicitRolls[modifier.modifier_id]?.[stat.stat_id] || ''])),
          ]))
          onChange({ ...current, itemName: base.name || base.base_item_id, implicitRolls })
        }
      })
      .catch(error => !cancelled && onError(error instanceof Error ? error.message : String(error)))
      .finally(() => !cancelled && setLoadingBases(false))
    return () => { cancelled = true }
  }, [apiUrl, slot?.slot_id])

  useEffect(() => {
    setAffixes([])
    setAffixSearch('')
    if (!slot || !current.baseItemId) return
    const itemLevel = Number(current.itemLevel)
    if (!Number.isInteger(itemLevel) || itemLevel < 1 || itemLevel > 100) return
    let cancelled = false
    setLoadingAffixes(true)
    const query = new URLSearchParams({ base_item_id: current.baseItemId, item_level: String(itemLevel) })
    fetch(`${apiUrl}/api/catalog/equipment/slots/${encodeURIComponent(slot.slot_id)}/affixes?${query}`)
      .then(response => {
        if (!response.ok) throw new Error(`Fixed equipment affix catalog failed (${response.status})`)
        return response.json() as Promise<Affix[]>
      })
      .then(rows => {
        if (cancelled) return
        setAffixes(rows)
        const definitions = new Map(rows.map(row => [row.modifier_id, row]))
        const selectedAffixIds = current.selectedAffixIds.filter(id => definitions.has(id)).slice(0, 6)
        const affixSources = Object.fromEntries(selectedAffixIds.map(id => [id, definitions.get(id)!.source_kind])) as Record<string, AffixSourceKind>
        const affixRolls = Object.fromEntries(selectedAffixIds.map(id => {
          const definition = definitions.get(id)!
          return [id, Object.fromEntries(definition.stats.map(stat => [stat.stat_id, current.affixRolls[id]?.[stat.stat_id] || '']))]
        }))
        if (
          selectedAffixIds.join('|') !== current.selectedAffixIds.join('|') ||
          selectedAffixIds.some(id => current.affixSources[id] !== affixSources[id])
        ) onChange({ ...current, selectedAffixIds, affixSources, affixRolls })
      })
      .catch(error => !cancelled && onError(error instanceof Error ? error.message : String(error)))
      .finally(() => !cancelled && setLoadingAffixes(false))
    return () => { cancelled = true }
  }, [apiUrl, slot?.slot_id, current.baseItemId, current.itemLevel])

  const filteredBases = useMemo(() => {
    const needle = search.trim().toLowerCase()
    if (!needle) return bases
    return bases.filter(base => `${base.name || ''} ${base.base_item_id} ${base.drop_level ?? ''}`.toLowerCase().includes(needle))
  }, [bases, search])
  const filteredAffixes = useMemo(() => {
    const needle = affixSearch.trim().toLowerCase()
    if (!needle) return affixes
    return affixes.filter(affix => [
      affix.name || '', affix.modifier_id, affix.source_kind,
      ...(affix.families || []), ...affix.stats.map(stat => stat.stat_id),
    ].some(value => value.toLowerCase().includes(needle)))
  }, [affixes, affixSearch])
  const selectedBase = bases.find(base => base.base_item_id === current.baseItemId)
  const selectedAffixes = current.selectedAffixIds.map(id => affixes.find(value => value.modifier_id === id)).filter((value): value is Affix => Boolean(value))
  const displayedBases = selectedBase && !filteredBases.some(base => base.base_item_id === selectedBase.base_item_id)
    ? [selectedBase, ...filteredBases]
    : filteredBases

  const chooseBase = (baseItemId: string) => {
    if (!baseItemId) { onClear(); return }
    const base = bases.find(row => row.base_item_id === baseItemId)
    if (!base) return
    onChange({
      baseItemId,
      itemName: base.name || base.base_item_id,
      itemLevel: current.itemLevel || '100',
      implicitRolls: Object.fromEntries(base.implicit_modifiers.map(modifier => [modifier.modifier_id, Object.fromEntries(modifier.stats.map(stat => [stat.stat_id, '']))])),
      selectedAffixIds: [],
      affixRolls: {},
      affixSources: {},
    })
    onError('')
  }

  const toggleAffix = (affix: Affix) => {
    if (current.selectedAffixIds.includes(affix.modifier_id)) {
      const selectedAffixIds = current.selectedAffixIds.filter(id => id !== affix.modifier_id)
      const affixRolls = { ...current.affixRolls }
      const affixSources = { ...current.affixSources }
      delete affixRolls[affix.modifier_id]
      delete affixSources[affix.modifier_id]
      onChange({ ...current, selectedAffixIds, affixRolls, affixSources })
      onError('')
      return
    }
    const reason = conflictReason(affix, selectedAffixes)
    if (reason) { onError(reason); return }
    onChange({
      ...current,
      selectedAffixIds: [...current.selectedAffixIds, affix.modifier_id],
      affixRolls: { ...current.affixRolls, [affix.modifier_id]: Object.fromEntries(affix.stats.map(stat => [stat.stat_id, ''])) },
      affixSources: { ...current.affixSources, [affix.modifier_id]: affix.source_kind },
    })
    onError('')
  }

  if (!slot) return <div className="pp-fixed-empty">Select an enabled armour or jewellery slot to configure it.</div>

  return <section className="pp-fixed-editor" aria-label={`${slot.item_class_name} editor`}>
    <header>
      <div><small>{slot.slot_id}</small><strong>{slot.item_class_name}</strong></div>
      <span>Exact implicits · source-closed rare affixes</span>
    </header>
    <div className="pp-fixed-fields">
      <label>Find base<input type="search" value={search} placeholder={`Search ${bases.length || ''} calculation-active bases`} onChange={event => setSearch(event.target.value)} /></label>
      <label>Base item<select value={current.baseItemId} disabled={loadingBases} onChange={event => chooseBase(event.target.value)}><option value="">{loadingBases ? 'Loading bases…' : 'No item selected'}</option>{displayedBases.map(base => <option key={base.base_item_id} value={base.base_item_id}>{base.name || base.base_item_id} · drop {base.drop_level ?? '?'}</option>)}</select></label>
      <label>Item level<input type="number" min="1" max="100" value={current.itemLevel} disabled={!current.baseItemId} onChange={event => onChange({ ...current, itemLevel: event.target.value })} /></label>
    </div>
    {selectedBase && <>
      <div className="pp-fixed-base-summary"><strong>{selectedBase.name || selectedBase.base_item_id}</strong><span>{selectedBase.item_class_name}</span><small>Requires {selectedBase.req_str || 0} Str · {selectedBase.req_dex || 0} Dex · {selectedBase.req_int || 0} Int</small></div>
      <div className="pp-fixed-rolls">
        <h4>Inherent implicit rolls</h4>
        {selectedBase.implicit_modifiers.length === 0 ? <p>No inherent implicit modifier is present on this base.</p> : selectedBase.implicit_modifiers.map(modifier => <section key={modifier.modifier_id}>
          <div><strong>{modifier.name || modifier.modifier_id || 'Implicit'}</strong><small>{modifier.modifier_id}</small></div>
          {modifier.stats.map(stat => <label key={`${modifier.modifier_id}:${stat.stat_id}`}><span><code>{stat.stat_id}</code><small>{stat.min_value ?? '?'}–{stat.max_value ?? '?'}</small></span><input type="number" min={stat.min_value ?? undefined} max={stat.max_value ?? undefined} placeholder="Exact roll" value={current.implicitRolls[modifier.modifier_id]?.[stat.stat_id] || ''} onChange={event => onChange({ ...current, implicitRolls: { ...current.implicitRolls, [modifier.modifier_id]: { ...(current.implicitRolls[modifier.modifier_id] || {}), [stat.stat_id]: event.target.value } } })} /></label>)}
        </section>)}
      </div>

      <div className="pp-fixed-affixes">
        <div className="pp-fixed-affix-head"><strong>Rare explicit modifiers</strong><span>{current.selectedAffixIds.length}/6 · max 3 prefix / 3 suffix · max 1 craft</span></div>
        <label>Filter source-proven affixes<input type="search" value={affixSearch} disabled={loadingAffixes} placeholder={loadingAffixes ? 'Loading affixes…' : 'Name, stat, family…'} onChange={event => setAffixSearch(event.target.value)} /></label>
        {!selectedBase.natural_affixes_supported && <p className="pp-fixed-boundary">This exact base is outside the pinned-PoB natural spawn-tag closure. Natural affixes stay fail-closed; independently source-proven crafted modifiers remain available.</p>}
        <div className="pp-fixed-affix-columns">
          {(['PREFIX', 'SUFFIX'] as const).map(kind => <section key={kind}>
            <header><strong>{kind === 'PREFIX' ? 'Prefixes' : 'Suffixes'}</strong><span>{selectedAffixes.filter(affix => affix.generation_type_id === kind).length}/3</span></header>
            <div>{filteredAffixes.filter(affix => affix.generation_type_id === kind).map(affix => {
              const active = current.selectedAffixIds.includes(affix.modifier_id)
              const reason = conflictReason(affix, selectedAffixes)
              return <button type="button" key={affix.modifier_id} className={active ? 'selected' : reason ? 'blocked' : ''} title={reason || affix.modifier_id} onClick={() => toggleAffix(affix)}>
                <i>{active ? '✓' : '+'}</i><span><strong>{affix.name || affix.modifier_id}</strong><small>{affix.source_kind} · req {affix.required_level ?? 1} · {affix.stats.map(stat => `${stat.stat_id} ${stat.min_value ?? '?'}–${stat.max_value ?? '?'}`).join(' · ')}</small></span>
              </button>
            })}</div>
          </section>)}
        </div>
        {selectedAffixes.length > 0 && <div className="pp-fixed-explicit-rolls">{selectedAffixes.map(affix => <section key={affix.modifier_id}>
          <header><strong>{affix.name || affix.modifier_id}</strong><span>{affix.source_kind} · {affix.generation_type_id}</span></header>
          {affix.stats.map(stat => <label key={`${affix.modifier_id}:${stat.stat_id}`}><span><code>{stat.stat_id}</code><small>{stat.min_value ?? '?'}–{stat.max_value ?? '?'}</small></span><input type="number" min={stat.min_value ?? undefined} max={stat.max_value ?? undefined} placeholder="Exact roll" value={current.affixRolls[affix.modifier_id]?.[stat.stat_id] || ''} onChange={event => onChange({ ...current, affixRolls: { ...current.affixRolls, [affix.modifier_id]: { ...(current.affixRolls[affix.modifier_id] || {}), [stat.stat_id]: event.target.value } } })} /></label>)}
        </section>)}</div>}
      </div>

      <p className="pp-fixed-boundary">Only target-patch natural affixes that survive the pinned-PoB first-match spawn-weight rule and crafted modifiers with explicit target-patch item-class restrictions are shown. Every modifier’s complete stat set must already be implemented. Defensive/resistance stats are not silently discarded.</p>
      <button type="button" className="pp-fixed-clear" onClick={onClear}>Remove {slot.item_class_id}</button>
    </>}
  </section>
}
